import React, { useMemo, useState } from "react";

import { UnsavedChangesModal } from "../../../../components/modals/UnsavedChangesModal/unsaved-changes-modal.component";
import { UnderNdaBanner } from "../../../../features/common-attributes/components/under-nda-banner/under-nda-banner.component";
import { Tabs } from "../../../../components/layout/tabs/components/tabs/tabs.component";
import { isNavigatingAwayFrom } from "../../../../components/layout/tabs/components/tabs/tabs.functions";

import { WorkOrderAssetsTab } from "../../features/work-order-assets-tab/components/work-order-assets.component";
import { getWorkOrderTemplateView } from "../../types/work-order-template.functions";

import { WorkOrderProductsTab } from "../work-order-products-tab/work-order-products-tab.component";
import { WorkOrderApprovalTab } from "../work-order-approval-tab/work-order-approval-tab.component";
import { WorkOrderCommentsTab } from "../work-order-comments-tab/work-order-comments-tab.component";

import { WorkOrderFormProps } from "./work-order-form.props";
import { HiddenHeading } from "../../../pages.styles";

export const WorkOrderForm = (props: WorkOrderFormProps) => {
  const { forceOpen = false, headerHeight = 0, isDirty = false, template } = props;

  const TemplateView = useMemo(() => (template ? getWorkOrderTemplateView(template) : null), [
    template,
  ]);

  const [forceOpenOnProductsTab, setForceOpenOnProductsTab] = useState<false | (() => any)>(false);
  const [productsFormIsDirty, setProductsFormIsDirty] = useState<boolean>(false);

  if (!TemplateView) {
    return null;
  }

  const { isUnderNda, underNdaUserDetails } = props.ndaData;

  const shouldChangeProductTab = (newTabValue: number, changeTab: () => void) => {
    if (productsFormIsDirty && isNavigatingAwayFrom(1, props.currentTabValue, newTabValue)) {
      setForceOpenOnProductsTab(() => (response: boolean) => {
        setForceOpenOnProductsTab(false);

        if (response) {
          setProductsFormIsDirty(false);
          changeTab();
        }
      });

      return false;
    }

    return props.shouldChangeTab(newTabValue, changeTab);
  };

  const when = (): boolean => productsFormIsDirty && !forceOpenOnProductsTab;
  const tabPageTitles = ["Overview", "Products", "Approvals", "Assets", "Comments"];

  const tabs: { title: string; contents: any }[] = [
    {
      title: tabPageTitles[0],
      contents: (
        <>
          <UnsavedChangesModal forceOpen={forceOpen} when={isDirty} />
          <UnderNdaBanner
            showIf={Boolean(props.ndaData.isUnderNda)}
            underNdaUserDetails={props.ndaData.underNdaUserDetails}
          />
          <TemplateView
            form={props.form}
            pageTitle={tabPageTitles[0]}
            resourceId={props.resourceId}
            resourceType={template}
            userPermissions={props.userPermissions}
          />
        </>
      ),
    },
    {
      title: "Products",
      contents: (
        <>
          <UnsavedChangesModal forceOpen={forceOpenOnProductsTab} when={when()} />
          <WorkOrderProductsTab
            endDate={props.defaultValues.endAt}
            pageTitle={tabPageTitles[1]}
            workOrderId={props.resourceId}
            setProductsFormIsDirty={setProductsFormIsDirty}
          />
        </>
      ),
    },
    {
      title: "Approvals",
      contents: <WorkOrderApprovalTab status={props.status} pageTitle={tabPageTitles[2]} />,
    },
    {
      title: "Assets",
      contents: (
        <>
          <HiddenHeading>{tabPageTitles[3]}</HiddenHeading>
          <WorkOrderAssetsTab />
        </>
      ),
    },
    {
      title: "Comments",
      contents: <WorkOrderCommentsTab pageTitle={tabPageTitles[4]} />,
    },
  ];

  return (
    <Tabs
      currentTabValue={props.currentTabValue}
      shouldChangeTab={shouldChangeProductTab}
      handleTabChange={props.handleTabChange}
      headerHeight={headerHeight}
      isUnderNda={isUnderNda}
      tabs={tabs}
      underNdaUserDetails={underNdaUserDetails}
    />
  );
};
